import axios from 'axios';
import { getDeviceId, getFingerprint, getDeviceModel } from '@/lib/device';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

// withCredentials makes the browser send the httpOnly `ee_session` cookie
// on every API call. Auth tokens are NO LONGER stored in localStorage — the
// only thing kept client-side is `ee_role` so the auth bootstrap knows
// which /me endpoint to call.
export const api = axios.create({
    baseURL: API_BASE,
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
});

// Block all mutating requests while an admin is in a VIEW-ONLY impersonation
// session, so accidental clicks can never change the user's data. Rejects
// before hitting the network with a friendly detail the call-site toasts.
const READONLY_ALLOWED = ['/auth/stop-impersonation', '/auth/logout', '/auth/admin/logout'];
api.interceptors.request.use((config) => {
    try {
        config.headers['X-Device-Id'] = getDeviceId();
        config.headers['X-Device-Fp'] = getFingerprint();
        const model = getDeviceModel();
        if (model) config.headers['X-Device-Model'] = model;
    } catch (_) { /* ignore */ }
    let imp = null;
    try { imp = JSON.parse(localStorage.getItem('ee_impersonating') || 'null'); } catch (_) { /* ignore */ }
    if (imp && imp.readonly) {
        const method = (config.method || 'get').toLowerCase();
        const url = config.url || '';
        const isMutation = ['post', 'put', 'patch', 'delete'].includes(method);
        const allowed = READONLY_ALLOWED.some((p) => url.includes(p));
        if (isMutation && !allowed) {
            return Promise.reject({
                response: { status: 403, data: { detail: 'View-only support session — changes are disabled. Exit view-only mode to make changes.' } },
            });
        }
    }
    return config;
});

api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            const role = localStorage.getItem('ee_role');
            // Don't auto-redirect from login pages
            const path = window.location.pathname;
            if (!path.endsWith('/login') && !path.endsWith('/admin/login') && !path.endsWith('/reseller/login')) {
                localStorage.removeItem('ee_role');
                if (role === 'admin') window.location.href = '/admin/login';
                else if (role === 'reseller') window.location.href = '/reseller/login';
                else window.location.href = '/login';
            }
        }
        return Promise.reject(error);
    }
);

export function formatApiError(detail) {
    if (detail == null) return 'Something went wrong';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e)))
            .join(' ');
    if (detail && typeof detail.msg === 'string') return detail.msg;
    return String(detail);
}

export async function downloadFile(url, filename) {
    const res = await api.get(url, { responseType: 'blob' });
    const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename || 'invoice.pdf';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => window.URL.revokeObjectURL(blobUrl), 1000);
}
